import React, { useEffect, useRef, useState } from 'react';
import HanziWriter from 'hanzi-writer';
import { useGameStore } from '../store/gameStore';
import { useAuthStore, isAdmin } from '../store/authStore';
import { lessonsApi } from '../api/lessons';
import { sentencesApi } from '../api/client';
import { splitPinyinSyllables } from '../utils/pinyin';

const EMPTY_FIELDS = { pinyin: '', radical: '', radical_meaning: '', meaning: '' };

const StrokeModal = () => {
  const {
    strokeModalOpen,
    closeStrokeModal,
    currentStrokeChar,
    lessons,
    currentLessonIndex,
    currentSentenceIndex,
    updateBreakdownChar,
  } = useGameStore();
  const { user } = useAuthStore();
  const writerRef = useRef(null);
  const containerRef = useRef(null);
  const [mode, setMode] = useState('animate'); // 'animate' or 'quiz'
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(EMPTY_FIELDS);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const lesson = lessons[currentLessonIndex];
  const sentence = lesson?.dialogue?.[currentSentenceIndex];
  const charIndex = currentStrokeChar?.origIndex;

  // The tile itself carries the breakdown fields (pinyin/radical/meaning) and
  // its origIndex, so prefer the precise breakdown entry by index, falling back
  // to the tile's own fields.
  const charInfo =
    (charIndex != null && sentence?.breakdown?.[charIndex]) || currentStrokeChar || null;

  const isUserSentence = lesson?.code === 'MY-SENTENCES';
  const canEdit = !!user && charIndex != null && (
    isUserSentence
      ? !!sentence?.sentenceId
      : (isAdmin(user) || lesson?.createdById === user.id)
  );

  // Fall back to the sentence-level pinyin (split by syllable) when the
  // per-character breakdown has no pinyin yet — matches what the tile shows.
  const fallbackPinyin =
    charIndex != null
      ? splitPinyinSyllables(sentence?.pinyin || '')[charIndex] || ''
      : '';

  // Reset edit state whenever the modal target changes.
  useEffect(() => {
    setEditing(false);
    setSaveError(null);
    setDraft({
      pinyin: charInfo?.pinyin || fallbackPinyin || '',
      radical: charInfo?.radical || '',
      radical_meaning: charInfo?.radical_meaning || '',
      meaning: charInfo?.meaning || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStrokeChar, strokeModalOpen]);

  const handleSave = async () => {
    if (charIndex == null) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = updateBreakdownChar(currentSentenceIndex, charIndex, draft);
      if (!result) throw new Error('Could not locate character to update.');

      if (isUserSentence) {
        // Persist the whole breakdown array on the user sentence.
        await sentencesApi.update(sentence.sentenceId, { breakdown: result.sentence.breakdown });
      } else if (charInfo?.id) {
        // Persist the single DB-backed character.
        await lessonsApi.updateCharacter(charInfo.id, draft);
      }
      setEditing(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };
  
  useEffect(() => {
    if (!strokeModalOpen || !containerRef.current || !currentStrokeChar) return;
    
    // Create HanziWriter instance
    writerRef.current = HanziWriter.create(containerRef.current, currentStrokeChar.char, {
      width: 200,
      height: 200,
      padding: 5,
      strokeColor: '#e2e8f0',
      radicalColor: '#f87171',
      outlineColor: '#475569',
      drawingColor: '#6366f1',
      showOutline: true,
      strokeAnimationSpeed: 1.2,
      delayBetweenStrokes: 300,
    });
    
    if (mode === 'animate') {
      writerRef.current.animateCharacter();
    } else {
      writerRef.current.quiz({
        onMistake: () => {},
        onCorrectStroke: () => {},
        onComplete: (summary) => {
          console.log('Quiz complete:', summary);
        }
      });
    }
    
    return () => {
      if (writerRef.current) {
        writerRef.current.cancelQuiz();
        writerRef.current = null;
      }
    };
  }, [strokeModalOpen, currentStrokeChar, mode]);
  
  const handleAnimate = () => {
    setMode('animate');
    if (writerRef.current) {
      writerRef.current.cancelQuiz();
      writerRef.current.animateCharacter();
    }
  };
  
  const handleQuiz = () => {
    setMode('quiz');
    if (writerRef.current) {
      writerRef.current.cancelQuiz();
      writerRef.current.quiz({
        onMistake: () => {},
        onCorrectStroke: () => {},
        onComplete: (summary) => {
          console.log('Quiz complete:', summary);
        }
      });
    }
  };
  
  if (!strokeModalOpen) return null;
  
  const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 101,
  };
  
  const modalStyle = {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '16px',
    padding: '28px',
    width: '90%',
    maxWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '18px',
  };
  
  const charDisplayStyle = {
    fontSize: '4rem',
    color: '#f1f5f9',
    lineHeight: 1,
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  
  const infoStyle = {
    display: 'flex',
    gap: '16px',
    fontSize: '0.85rem',
    color: '#94a3b8',
    flexWrap: 'wrap',
    justifyContent: 'center',
  };
  
  const containerStyle = {
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#0f172a',
  };
  
  const controlsStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  };
  
  const buttonStyle = (active) => ({
    background: active ? '#6366f1' : '#334155',
    border: 'none',
    color: '#e2e8f0',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  });
  
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeStrokeModal();
    }
  };
  
  return (
    <div 
      className="stroke-overlay open" 
      style={modalOverlayStyle}
      onClick={handleOverlayClick}
    >
      <div className="stroke-modal" style={modalStyle}>
        <div style={charDisplayStyle}>
          {currentStrokeChar?.char}
        </div>
        
        {!editing && (
          <div style={infoStyle}>
            <span>Pinyin: <strong style={{ color: '#e2e8f0' }}>{charInfo?.pinyin || '—'}</strong></span>
            <span>Radical: <strong style={{ color: '#e2e8f0' }}>{charInfo?.radical || '—'}{charInfo?.radical_meaning ? ` (${charInfo.radical_meaning})` : ''}</strong></span>
            <span>Meaning: <strong style={{ color: '#e2e8f0' }}>{charInfo?.meaning || '—'}</strong></span>
          </div>
        )}

        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
            {[
              { key: 'pinyin', label: 'Pinyin' },
              { key: 'radical', label: 'Radical' },
              { key: 'radical_meaning', label: 'Radical meaning' },
              { key: 'meaning', label: 'Meaning' },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem', color: '#94a3b8' }}>
                {label}
                <input
                  value={draft[key]}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  style={{
                    background: '#0f172a',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#e2e8f0',
                    padding: '8px 10px',
                    fontSize: '0.9rem',
                  }}
                />
              </label>
            ))}
            {saveError && <div style={{ color: '#f87171', fontSize: '0.8rem' }}>{saveError}</div>}
          </div>
        )}

        {canEdit && (
          <div style={controlsStyle}>
            {!editing ? (
              <button onClick={() => setEditing(true)} style={buttonStyle(false)}>
                ✎ Edit info
              </button>
            ) : (
              <>
                <button onClick={handleSave} disabled={saving} style={buttonStyle(true)}>
                  {saving ? 'Saving…' : '💾 Save'}
                </button>
                <button onClick={() => { setEditing(false); setSaveError(null); }} disabled={saving} style={buttonStyle(false)}>
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
        
        <div ref={containerRef} style={containerStyle} id="hanzi-writer-container" />
        
        <div style={controlsStyle}>
          <button 
            onClick={handleAnimate}
            style={buttonStyle(mode === 'animate')}
          >
            ▶ Animate
          </button>
          <button 
            onClick={handleQuiz}
            style={buttonStyle(mode === 'quiz')}
          >
            ✎ Quiz
          </button>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', width: '100%' }}>
          <button
            onClick={closeStrokeModal}
            style={{
              background: '#334155',
              color: '#e2e8f0',
              padding: '8px 20px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StrokeModal;
